import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { animate, motion, useInView, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';

/**
 * Motion primitives shared by the landing page and the app.
 *
 * Three rules hold everywhere in here:
 *
 * 1. Every animation is entrance or state-change. Nothing loops for
 *    decoration; the only continuous motion in the product is the tape and
 *    the live dot, both of which mean "this is moving right now".
 * 2. Nothing moves more than ~14px or lasts longer than ~700ms. Subtlety is
 *    the brief — a trading screen that swoops is a trading screen nobody
 *    trusts.
 * 3. `prefers-reduced-motion` is honoured by rendering the *destination*
 *    state immediately rather than by playing a faster animation, so a
 *    reduced-motion visitor sees a finished page, never a stuck one.
 */

/** True when the visitor has not asked for reduced motion. */
export function useMotionOk(): boolean {
  return !useReducedMotion();
}

const HIDDEN = { opacity: 0, y: 14, filter: 'blur(6px)' };
const SHOWN = { opacity: 1, y: 0, filter: 'blur(0px)' };
const RISE: Variants = { hidden: HIDDEN, shown: SHOWN };

/**
 * Fade + rise + un-blur as the element scrolls into view, once.
 *
 * The blur is what stops this reading as the default framer fade: text
 * resolving into focus is the same gesture as a print coming up in a tray,
 * which is the whole aesthetic.
 */
export function Reveal({
  children,
  delay = 0,
  y = 14,
  className,
  style,
  as = 'div',
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  style?: CSSProperties;
  as?: 'div' | 'span' | 'section' | 'li' | 'h2' | 'p';
}) {
  const ok = useMotionOk();
  const Tag = motion[as] as typeof motion.div;
  if (!ok) {
    const Plain = as as 'div';
    return (
      <Plain className={className} style={style}>
        {children}
      </Plain>
    );
  }
  return (
    <Tag
      className={className}
      style={style}
      variants={{ hidden: { ...HIDDEN, y }, shown: SHOWN }}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, margin: '-12% 0px -8% 0px' }}
      transition={{ duration: 0.62, delay, ease: [0.2, 0.7, 0.2, 1] }}
    >
      {children}
    </Tag>
  );
}

/**
 * Container that reveals its children one after another. Children must be
 * `<StaggerItem>`s; the delay lives on the parent so a list does not have to
 * hand-count indices.
 */
export function Stagger({
  children,
  className,
  style,
  gap = 0.07,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  gap?: number;
  delay?: number;
}) {
  const ok = useMotionOk();
  if (!ok) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, margin: '-10% 0px' }}
      variants={{ shown: { transition: { staggerChildren: gap, delayChildren: delay } } }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const ok = useMotionOk();
  if (!ok) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      style={style}
      variants={RISE}
      transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}

/**
 * A figure that counts up to its value the first time it is seen.
 *
 * `format` receives the intermediate value, so the same component prints
 * rupees, percentages and plain counts without knowing about any of them.
 * The count is driven by motion's `animate` writing to the node directly —
 * re-rendering React sixty times a second to move one number would be a
 * waste on a page that has several.
 */
export function CountUp({
  to,
  from = 0,
  duration = 1.1,
  format = (v: number) => v.toFixed(0),
  className,
}: {
  to: number;
  from?: number;
  duration?: number;
  format?: (v: number) => string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const ok = useMotionOk();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!ok || !inView) {
      if (!ok) node.textContent = format(to);
      return;
    }
    const controls = animate(from, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        node.textContent = format(v);
      },
    });
    return () => controls.stop();
  }, [inView, ok, from, to, duration, format]);

  // The destination is what renders before the element is scrolled to. A
  // figure sitting at its starting value ("9" under a label about zero) reads
  // as a wrong number to anyone who never scrolls it into view; `animate`
  // writes the opening frame itself the moment it starts, so nothing flashes.
  return (
    <span ref={ref} className={className}>
      {format(to)}
    </span>
  );
}

/**
 * An infinite horizontal tape. The track is rendered twice and translated by
 * exactly half its width, which is the only way to loop without a seam at
 * arbitrary content widths. Pauses on hover so a reader can actually read it.
 */
export function Marquee({
  children,
  seconds = 42,
  className,
}: {
  children: ReactNode;
  seconds?: number;
  className?: string;
}) {
  const ok = useMotionOk();
  return (
    <div className={`mq${className ? ` ${className}` : ''}`} data-static={ok ? undefined : ''}>
      <div className="mq-track" style={{ animationDuration: `${seconds}s` }}>
        {children}
        <span aria-hidden>{children}</span>
      </div>
    </div>
  );
}

/**
 * Types a string out one character at a time, once, when scrolled into view.
 * Used for exactly one line on the landing page (the receipt) — a typing
 * effect anywhere else would be theatre.
 */
export function TypeLine({
  text,
  speed = 26,
  className,
  cursor = true,
}: {
  text: string;
  speed?: number;
  className?: string;
  cursor?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const ok = useMotionOk();
  const [n, setN] = useState(0);
  const done = n >= text.length;

  useEffect(() => {
    if (!ok || !inView) return;
    if (n >= text.length) return;
    const t = setTimeout(() => setN((c) => c + 1), speed);
    return () => clearTimeout(t);
  }, [ok, inView, n, text.length, speed]);

  return (
    <span ref={ref} className={className}>
      {ok ? text.slice(0, n) : text}
      {cursor && ok && !done ? <span className="type-caret" aria-hidden /> : null}
    </span>
  );
}
